import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../stores/redux.store';
import {
  useAnalyticsClient,
  useFormSubmittedEvent,
} from '../../../../../utilities/analytics/analytics-web-client';
import { initiateOtp } from '../../../../../actions/initiate-otp-actions';
import { useRegisterComponent } from '../../../../orchestrator';
import { INITIATE_OTP_PAGE_ID } from '../constants';
import { useNavigateSoftOrHard } from '../../../../../utilities/hooks/useNavigateSoftOrHard';
import { SendOtpEmailInterstitialErrorCode } from '../../../../../components/SendOtpEmailInterstitial/SendOtpEmailInterstitial';

type InitiateOtpHookProps = {
  onSubmit?: () => void;
};

export const useInitiateOtp = ({ onSubmit }: InitiateOtpHookProps = {}) => {
  useRegisterComponent(useInitiateOtp.name);

  const { redirectType, error, isLoading, redirectTo } = useAppSelector(
    state => state.verifyEmailOtp
  );

  const dispatch = useAppDispatch();
  const analyticsClient = useAnalyticsClient();
  const navigate = useNavigateSoftOrHard();

  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo);
    }
  }, [redirectTo, navigate]);

  const trackSubmit = useFormSubmittedEvent(INITIATE_OTP_PAGE_ID, 'initiateOtp', {
    signupType: redirectType,
    source: 'initiateOtpScreen',
  });

  const onSendVerificationEmail = useCallback(() => {
    onSubmit?.();
    trackSubmit();
    dispatch(initiateOtp(INITIATE_OTP_PAGE_ID, analyticsClient));
  }, [analyticsClient, dispatch, onSubmit, trackSubmit]);

  const errorCode: SendOtpEmailInterstitialErrorCode | undefined = useMemo(() => {
    if (!error) {
      return undefined;
    }
    switch (error) {
      case 'rate_limit_exceeded':
      case 'Too many requests. Please inspect Retry-After header.':
        return 'rate_limited';
      case 'internal_server_error':
        return 'internal_server_error';
      default:
        return 'unexpected_error';
    }
  }, [error]);

  return {
    onSendVerificationEmail,
    errorCode,
    isLoading,
  };
};
